import * as XLSX from "xlsx";

// Parses a GSTN portal GSTR-2A/2B Excel export entirely client-side (see
// Phase 2 plan: the server has no xlsx/multer dependency by design — this
// keeps the request body to structured JSON, well under the 10mb limit,
// instead of shipping the raw workbook to the backend).
//
// Real GSTN exports vary: 1-6 banner rows before the real header, slightly
// different column wording/order across portal versions, and multiple
// sheets (B2B, CDNR, amendments, ISD, IMPG). Phase 2 scope is B2B and CDNR
// only (see plan) — other sheets are ignored, not errored on.

// Recognizable header tokens, matched case-insensitively against each
// candidate row's cells — real position/order varies release to release,
// so this never assumes a fixed column index.
const HEADER_PATTERNS = {
  supplierGstin: /gstin.*supplier|supplier.*gstin/i,
  supplierName: /(trade|legal).*name|supplier.*name/i,
  invoiceNumber: /invoice.*number|inv.*no|note.*number/i,
  invoiceDate: /invoice.*date|note.*date/i,
  invoiceType: /invoice.*type/i,
  taxableValue: /taxable.*value/i,
  igst: /integrated.*tax|igst/i,
  cgst: /central.*tax|cgst/i,
  sgst: /state.*tax|ut.*tax|sgst/i,
};

const SHEET_DOC_CATEGORY = {
  B2B: "B2B",
  CDNR: "CDNR",
};

function normalizeHeaderCell(cell) {
  return String(cell ?? "").trim();
}

// Scans the first N rows of a sheet's AOA for the one that looks like a
// real header row — the row with the most recognizable token matches —
// rather than assuming any fixed banner-row offset.
function findHeaderRow(aoa, maxScanRows = 10) {
  let bestRow = -1;
  let bestScore = 0;

  for (let r = 0; r < Math.min(maxScanRows, aoa.length); r++) {
    const row = aoa[r] || [];
    let score = 0;
    for (const cell of row) {
      const text = normalizeHeaderCell(cell);
      if (!text) continue;
      for (const pattern of Object.values(HEADER_PATTERNS)) {
        if (pattern.test(text)) {
          score++;
          break;
        }
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestRow = r;
    }
  }

  // Require at least GSTIN + invoice number to trust this is a real
  // header — a row that only matches "taxable value" could be a subtotal
  // line, not the header.
  return bestScore >= 2 ? bestRow : -1;
}

function buildColumnMap(headerRow) {
  const map = {};
  headerRow.forEach((cell, colIndex) => {
    const text = normalizeHeaderCell(cell);
    if (!text) return;
    for (const [field, pattern] of Object.entries(HEADER_PATTERNS)) {
      if (map[field] === undefined && pattern.test(text)) {
        map[field] = colIndex;
      }
    }
  });
  return map;
}

// Excel/GSTN exports store dates as a serial day-count from 1899-12-30
// (Excel's epoch, including its deliberate 1900-leap-year bug — the
// well-known reason the epoch isn't 1899-12-31). Implemented directly
// instead of relying on XLSX.SSF, which isn't reliably exposed on the
// package's namespace export across bundler/Node ESM interop.
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function excelSerialToDate(value) {
  if (value instanceof Date) {
    // A JS Date parsed from a date-only string/cell — normalize to
    // UTC-midnight-of-that-calendar-date using its LOCAL fields (the
    // fields that reflect the intended calendar date), so
    // toDateOnlyString's UTC-field read below stays correct.
    return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
  }
  if (typeof value === "number") {
    return new Date(EXCEL_EPOCH_MS + value * MS_PER_DAY);
  }
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return null;
  return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
}

// Formats a Date as YYYY-MM-DD reading its UTC fields — never
// .toISOString().slice(0,10) after further conversion, and never local
// getFullYear()/getMonth()/getDate(), which would reintroduce the same
// off-by-one-day bug this replaces. Excel-serial dates above are built via
// Date.UTC, so their UTC fields are exactly the calendar date intended;
// the string fallback path is normalized to a UTC-midnight Date for the
// same reason before this is ever called (see parseSheet).
function toDateOnlyString(date) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

function parseSheet(sheetName, worksheet, docCategory) {
  const aoa = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true, defval: "" });
  const headerRowIndex = findHeaderRow(aoa);

  if (headerRowIndex === -1) {
    return { rows: [], errors: [] };
  }

  const columnMap = buildColumnMap(aoa[headerRowIndex]);
  if (columnMap.supplierGstin === undefined || columnMap.invoiceNumber === undefined) {
    return { rows: [], errors: [] };
  }

  const rows = [];
  const errors = [];

  for (let r = headerRowIndex + 1; r < aoa.length; r++) {
    const line = aoa[r];
    if (!line || line.every((c) => c === "" || c == null)) continue;

    const supplierGstin = String(line[columnMap.supplierGstin] ?? "").toUpperCase().trim();
    const invoiceNumber = String(line[columnMap.invoiceNumber] ?? "").trim();

    // A row with neither field populated is a subtotal/blank spacer row in
    // the export, not a real data row — skip silently rather than flag it.
    if (!supplierGstin && !invoiceNumber) continue;

    const rowNumber = r + 1; // 1-indexed, for user-facing error messages

    if (!GSTIN_REGEX.test(supplierGstin)) {
      errors.push({ sheet: sheetName, row: rowNumber, message: `Malformed GSTIN: "${supplierGstin || "(blank)"}"` });
      continue;
    }
    if (!invoiceNumber) {
      errors.push({ sheet: sheetName, row: rowNumber, message: "Missing invoice number" });
      continue;
    }

    const invoiceDate = columnMap.invoiceDate !== undefined ? excelSerialToDate(line[columnMap.invoiceDate]) : null;
    if (!invoiceDate) {
      errors.push({ sheet: sheetName, row: rowNumber, message: `Invalid/missing invoice date for ${invoiceNumber}` });
      continue;
    }

    rows.push({
      docCategory,
      supplierGstin,
      supplierName: columnMap.supplierName !== undefined ? String(line[columnMap.supplierName] ?? "").trim() : "",
      invoiceNumber,
      invoiceDate: toDateOnlyString(invoiceDate),
      invoiceType: columnMap.invoiceType !== undefined ? String(line[columnMap.invoiceType] ?? "").trim() : "",
      taxableValue: columnMap.taxableValue !== undefined ? Number(line[columnMap.taxableValue]) || 0 : 0,
      igst: columnMap.igst !== undefined ? Number(line[columnMap.igst]) || 0 : 0,
      cgst: columnMap.cgst !== undefined ? Number(line[columnMap.cgst]) || 0 : 0,
      sgst: columnMap.sgst !== undefined ? Number(line[columnMap.sgst]) || 0 : 0,
    });
  }

  return { rows, errors };
}

// Parses a raw GSTN 2A/2B workbook (an ArrayBuffer, as read via
// FileReader.readAsArrayBuffer) into { rows, errors, sheetsFound }. Only
// sheets whose name matches a known B2B/CDNR pattern are read — every
// other sheet (amendments, ISD, IMPG, summary) is silently skipped, since
// Phase 2 scope is B2B/CDNR only (see plan).
export function parseGstr2Workbook(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: false });

  const allRows = [];
  const allErrors = [];
  const sheetsFound = [];

  for (const sheetName of workbook.SheetNames) {
    const normalizedName = sheetName.toUpperCase();
    let docCategory = null;
    if (/B2B/.test(normalizedName) && !/AMEND/.test(normalizedName)) {
      docCategory = SHEET_DOC_CATEGORY.B2B;
    } else if (/CDNR/.test(normalizedName) && !/AMEND/.test(normalizedName)) {
      docCategory = SHEET_DOC_CATEGORY.CDNR;
    }

    if (!docCategory) continue;

    const { rows, errors } = parseSheet(sheetName, workbook.Sheets[sheetName], docCategory);
    if (rows.length || errors.length) {
      sheetsFound.push(sheetName);
      allRows.push(...rows);
      allErrors.push(...errors);
    }
  }

  return { rows: allRows, errors: allErrors, sheetsFound };
}
